import { useState, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { FolderOpen, Upload, Download, Check, X, RefreshCw, Plus, Mail, Loader2, Eye } from 'lucide-react';
import { Button } from '../../ui/button';
import { requiredDocumentCategories, financeRules } from '../../../utils/financeType';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const STATUS_CONFIG = {
  pending: { label: 'Required', color: 'bg-gray-500/20 text-gray-400 border-gray-500/30' },
  received: { label: 'Received', color: 'bg-blue-500/20 text-blue-400 border-blue-500/30' },
  verified: { label: 'Verified', color: 'bg-green-500/20 text-green-400 border-green-500/30' },
  rejected: { label: 'Rejected', color: 'bg-red-500/20 text-red-400 border-red-500/30' },
};

export default function DocumentsTab({ deal, dealId, onRefresh }) {
  const docs = deal.documents || [];
  const [generating, setGenerating] = useState(false);
  const [uploading, setUploading] = useState(null);
  const [newDocName, setNewDocName] = useState('');
  const [showAdd, setShowAdd] = useState(false);
  const fileRef = useRef({});

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const res = await axios.post(`${API}/crm/deals/${dealId}/doc-checklist/generate`);
      toast.success(`Checklist updated. ${res.data.created} new items added.`);
      onRefresh();
    } catch (e) { toast.error('Failed to generate checklist'); }
    finally { setGenerating(false); }
  };

  const handleUpload = async (file, docId, category) => {
    if (!file || file.size > 10 * 1024 * 1024) { toast.error('File must be under 10MB'); return; }
    setUploading(docId);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('deal_id', dealId);
      fd.append('category', category || 'other');
      if (docId) fd.append('doc_id', docId);
      await axios.post(`${API}/crm/documents/upload`, fd);
      toast.success(`${file.name} uploaded`);
      onRefresh();
    } catch (e) { toast.error('Upload failed'); }
    finally { setUploading(null); }
  };

  const handleStatusChange = async (docId, isReceived) => {
    try {
      await axios.put(`${API}/crm/documents/${docId}?is_received=${isReceived}`);
      onRefresh();
    } catch (e) { toast.error('Failed to update'); }
  };

  const handleDownload = async (docId, filename) => {
    try {
      const res = await axios.get(`${API}/crm/documents/${docId}/download`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a'); a.href = url; a.download = filename || 'download'; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error('Download failed'); }
  };

  const handleAddDoc = async () => {
    if (!newDocName.trim()) return;
    try {
      await axios.post(`${API}/crm/documents?deal_id=${dealId}&name=${encodeURIComponent(newDocName)}&file_type=other`);
      setNewDocName(''); setShowAdd(false);
      toast.success('Document added'); onRefresh();
    } catch (e) { toast.error('Failed to add'); }
  };

  const receivedCount = docs.filter(d => d.is_received).length;
  const rules = financeRules(deal.finance_type);
  const requiredCategories = requiredDocumentCategories(deal.finance_type, deal.business?.entity_type || deal.entity_type || '');
  const requiredDocs = docs.filter(d => requiredCategories.includes(d.category));
  const otherDocs = docs.filter(d => !requiredCategories.includes(d.category));

  return (
    <div className="max-w-4xl space-y-4" data-testid="documents-tab">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h2 className="text-white font-medium flex items-center gap-2">
          <FolderOpen className="h-5 w-5 text-cyan-400" /> Documents
          <span className="text-xs text-gray-500 font-normal">{receivedCount}/{docs.length} received</span>
        </h2>
        <div className="flex gap-2">
          <Button onClick={handleGenerate} disabled={generating} size="sm" variant="outline" className="border-white/10 text-gray-300 hover:bg-white/5" data-testid="generate-checklist-btn">
            <RefreshCw className={`h-4 w-4 mr-1 ${generating ? 'animate-spin' : ''}`} />Auto-Generate
          </Button>
          <Button onClick={() => setShowAdd(!showAdd)} size="sm" className="bg-purple-600 hover:bg-purple-700 text-white">
            <Plus className="h-4 w-4 mr-1" />Add Item
          </Button>
        </div>
      </div>

      {showAdd && (
        <div className="flex gap-2">
          <input value={newDocName} onChange={e => setNewDocName(e.target.value)} placeholder="Document name..."
            className="flex-1 bg-navy-800 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:border-purple-500 focus:outline-none" />
          <Button onClick={handleAddDoc} size="sm" className="bg-green-600 hover:bg-green-700 text-white">Add</Button>
        </div>
      )}

      {/* Progress bar */}
      <div className="w-full bg-white/5 rounded-full h-2">
        <div className="h-2 rounded-full bg-cyan-500 transition-all" style={{ width: `${docs.length ? (receivedCount / docs.length * 100) : 0}%` }} />
      </div>

      <div className="text-xs text-gray-400" data-testid="documents-requirement-hint">
        Required for <span className="text-white">{deal.finance_type || 'Asset Finance'}</span>:
        {' '}{requiredCategories.join(', ') || 'None'}
        {!rules.requiresAsset && ' (Asset quote not required)'}
      </div>

      {/* Required Documents */}
      <div className="space-y-2">
        {requiredDocs.map(doc => {
          const status = doc.is_received ? (doc.status === 'verified' ? 'verified' : 'received') : 'pending';
          const cfg = STATUS_CONFIG[status];
          return (
            <div key={doc.id} className="flex items-center gap-3 bg-navy-800/50 rounded-lg px-4 py-3 border border-white/5" data-testid={`doc-row-${doc.id}`}>
              <span className={`text-xs px-2 py-0.5 rounded-full border ${cfg.color}`}>{cfg.label}</span>
              <span className="flex-1 text-sm text-white">{doc.name}</span>
              {doc.original_filename && <span className="text-xs text-gray-500 hidden md:block">{doc.original_filename}</span>}
              <div className="flex items-center gap-1">
                {doc.storage_path && (
                  <button onClick={() => handleDownload(doc.id, doc.original_filename)} className="p-1.5 text-gray-400 hover:text-white rounded hover:bg-white/5">
                    <Download className="h-4 w-4" />
                  </button>
                )}
                {!doc.is_received ? (
                  <button onClick={() => handleStatusChange(doc.id, true)} className="p-1.5 text-gray-400 hover:text-green-400 rounded hover:bg-green-500/10" title="Mark received">
                    <Check className="h-4 w-4" />
                  </button>
                ) : (
                  <button onClick={() => handleStatusChange(doc.id, false)} className="p-1.5 text-green-400 hover:text-gray-400 rounded hover:bg-white/5" title="Mark not received">
                    <Check className="h-4 w-4" />
                  </button>
                )}
                <input type="file" ref={el => fileRef.current[doc.id] = el} className="hidden"
                  onChange={e => e.target.files[0] && handleUpload(e.target.files[0], doc.id, doc.category)} />
                <button onClick={() => fileRef.current[doc.id]?.click()} disabled={uploading === doc.id}
                  className="p-1.5 text-gray-400 hover:text-purple-400 rounded hover:bg-purple-500/10">
                  {uploading === doc.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Other Documents */}
      {otherDocs.length > 0 && (
        <>
          <h3 className="text-gray-400 text-xs uppercase mt-4">Other Documents</h3>
          <div className="space-y-2">
            {otherDocs.map(doc => (
              <div key={doc.id} className="flex items-center gap-3 bg-navy-800/30 rounded-lg px-4 py-2.5 border border-white/5" data-testid={`doc-row-${doc.id}`}>
                <span className="flex-1 text-sm text-gray-300">{doc.name}</span>
                {doc.storage_path && (
                  <button onClick={() => handleDownload(doc.id, doc.original_filename)} className="p-1.5 text-gray-400 hover:text-white rounded hover:bg-white/5">
                    <Download className="h-4 w-4" />
                  </button>
                )}
                <input type="file" ref={el => fileRef.current[doc.id] = el} className="hidden"
                  onChange={e => e.target.files[0] && handleUpload(e.target.files[0], doc.id, doc.category)} />
                <button onClick={() => fileRef.current[doc.id]?.click()} disabled={uploading === doc.id}
                  className="p-1.5 text-gray-400 hover:text-purple-400 rounded hover:bg-purple-500/10">
                  {uploading === doc.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                </button>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
